import type {
  CirclesParticle,
  CirclesParticleEmission,
} from './circlesRenderTypes';
import { PARTICLE_POOL_MAX } from './circlesRenderConstants';
import { colourForEmission } from './circlesRenderColour';
import type { RenderQuality } from '../../rendering/renderQuality';

/**
 * Particle pool with shape variety. Spawns on hit/death/milestone/tap
 * emissions, ages with gravity, fades to invisible. Capped at
 * {@link PARTICLE_POOL_MAX} so a long song can't grow the pool
 * unbounded.
 *
 * The pool is also shared with the kiai burst (in
 * {@link SceneRenderer.maybeSpawnKiaiBurst}) — that method pushes
 * directly into {@link particles} so this class doesn't need to know
 * about the kiai vocabulary. Treat external pushes as additive — the
 * pool cap is enforced lazily on each {@link emit}.
 */
export class ParticleSystem {
  readonly particles: CirclesParticle[] = [];
  private reducedMotion: boolean;
  private quality: RenderQuality;
  private particlePoolMax: number;

  constructor(reducedMotion: boolean, quality: RenderQuality) {
    this.reducedMotion = reducedMotion;
    this.quality = quality;
    this.particlePoolMax = Math.round(PARTICLE_POOL_MAX * quality.particleScale);
  }

  private scaleCount(baseCount: number): number {
    if (this.quality.particleScale >= 1) {
      return baseCount;
    }
    return Math.max(1, Math.round(baseCount * this.quality.particleScale));
  }

  emit(emission: CirclesParticleEmission): void {
    const baseCount =
      emission.type === 'death'
        ? 36
        : emission.type === 'milestone'
          ? 28
          : emission.type === 'hit_perfect'
            ? 22
            : emission.type === 'hit_good'
              ? 14
              : emission.type === 'hit_ok'
                ? 9
                : emission.type === 'hit_miss'
                  ? 14
                  : emission.type === 'tap'
                    ? 6
                    : 8;
    const count = this.reducedMotion
      ? Math.max(2, Math.round(baseCount * 0.25))
      : this.scaleCount(baseCount);
    const colour = emission.colour ?? colourForEmission(emission.type);
    if (this.particles.length > this.particlePoolMax) {
      this.particles.splice(0, this.particles.length - this.particlePoolMax);
    }
    const speedBase =
      emission.type === 'death'
        ? 280
        : emission.type === 'milestone'
          ? 240
          : emission.type === 'tap'
            ? 90
            : emission.type === 'hit_miss'
              ? 220
              : 160;
    // Shape mix per type — perfects + milestones get more round
    // sparkles (energetic, premium); misses + death stay squarey
    // (sharper, harsher); tap stays squares (subtle).
    const discChance =
      emission.type === 'hit_perfect' || emission.type === 'milestone'
        ? 0.65
        : emission.type === 'hit_good'
          ? 0.45
          : emission.type === 'hit_ok'
            ? 0.3
            : 0;
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = speedBase * (0.4 + Math.random());
      const life = 0.4 + Math.random() * 0.6;
      const shape: 'square' | 'disc' =
        Math.random() < discChance ? 'disc' : 'square';
      this.particles.push({
        x: emission.x,
        y: emission.y,
        vx: Math.cos(angle) * speed,
        vy:
          Math.sin(angle) * speed -
          (emission.type === 'hit_perfect' ? 60 : emission.type === 'milestone' ? 80 : 0),
        life,
        maxLife: life,
        colour,
        size: 2 + Math.random() * (emission.type === 'milestone' ? 4.5 : 3.5),
        shape,
        rot: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 12,
      });
    }
    // Perfect-only "spark" accents: 6 high-velocity, short-life
    // sparks radiating in a symmetric star pattern from the hit
    // centre. The regular particle cloud is omnidirectional + heavy
    // — sparks are linear + light, giving perfects a distinct
    // "snap" silhouette that good/ok don't share. Skipped under
    // reduced-motion (the burst already conveys success).
    if (emission.type === 'hit_perfect' && !this.reducedMotion) {
      const sparkCount = this.scaleCount(6);
      for (let i = 0; i < sparkCount; i++) {
        // Even angular spacing with a small jitter so the star looks
        // organic rather than mechanical.
        const angle = (i / sparkCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.25;
        const speed = 360 + Math.random() * 180;
        const life = 0.22 + Math.random() * 0.12;
        this.particles.push({
          x: emission.x,
          y: emission.y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life,
          maxLife: life,
          colour,
          size: 3 + Math.random() * 2,
          shape: 'disc',
          rot: 0,
          spin: 0,
        });
      }
    }
  }

  update(dt: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= 0.93;
      p.vy = p.vy * 0.93 + 200 * dt;
      p.rot += p.spin * dt;
      p.life -= dt;
      if (p.life <= 0) {
        this.particles.splice(i, 1);
      }
    }
  }

  /**
   * Render particles with shape variety. Squares get a small rotation
   * so the cloud reads as motion rather than a static pixel grid;
   * discs are round filled circles that pair with the bigger
   * shockwave/burst on perfect / milestone hits to make those moments
   * feel celebratory.
   *
   * Hot path: hoists the outer save/restore + global-state writes
   * outside the loop (per-particle save/restore was previously a
   * noticeable cost when particle counts were high — each call pushes
   * the whole context state stack including the surrounding DPR scale
   * + shake translate). Rotated squares pay a translate/rotate pair
   * and undo it manually so we never touch the matrix stack.
   */
  draw(ctx: CanvasRenderingContext2D): void {
    if (this.particles.length === 0) {
      return;
    }
    ctx.save();
    const useShadows = this.quality.particleShadows && !this.reducedMotion;
    for (const p of this.particles) {
      const alpha = Math.max(0, p.life / p.maxLife);
      if (alpha <= 0) {
        continue;
      }
      ctx.globalAlpha = alpha;
      ctx.fillStyle = p.colour;
      if (useShadows) {
        ctx.shadowColor = p.colour;
        ctx.shadowBlur = 8;
      }
      if (p.shape === 'disc') {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 0.6, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
        ctx.rotate(-p.rot);
        ctx.translate(-p.x, -p.y);
      }
    }
    ctx.restore();
  }
}
